import React from 'react';
import {
  SkillNode, SkillTreeData, PassiveAbilityDefinition, SkillEffectData,
  findPassiveDefinition, humanizeEffectKey, formatBonusValue,
} from '../types/skillTree';

interface Props {
  skill: SkillNode;
  position: { x: number; y: number };
  skillTreeData: SkillTreeData;
  passiveDefinitions: PassiveAbilityDefinition[];
  getSkillIcon: (skill: SkillNode) => string;
  parseSkillEffects: (json: string) => SkillEffectData | null;
}

const SkillTooltip: React.FC<Props> = ({
  skill, position, skillTreeData, passiveDefinitions, getSkillIcon, parseSkillEffects,
}) => {
  const effects = parseSkillEffects(skill.skillEffects);
  const passiveAbility = skill.isLearned && effects?.PassiveAbilities?.[0]
    ? findPassiveDefinition(passiveDefinitions, effects.PassiveAbilities[0])
    : null;

  return (
    <div
      className="skill-tooltip"
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: 1000,
      }}
    >
      <div className="tooltip-header">
        <div className="tooltip-icon">{getSkillIcon(skill)}</div>
        <div className="tooltip-title">{skill.name}</div>
        <div className="tooltip-category">{skill.skillCategory}</div>
      </div>

      <div className="tooltip-description">{skill.description}</div>

      <div className="tooltip-requirements">
        <div className="tooltip-requirement-item">
          <span className="tooltip-label">Required Level:</span>
          <span className={skill.requiredLevel <= (skillTreeData.characterLevel || 1) ? 'tooltip-value-met' : 'tooltip-value-unmet'}>
            Lv {skill.requiredLevel}
          </span>
        </div>
        <div className="tooltip-requirement-item">
          <span className="tooltip-label">Cost:</span>
          <span className={skillTreeData.talentPoints.available >= skill.talentPointCost ? 'tooltip-value-met' : 'tooltip-value-unmet'}>
            {skill.talentPointCost} TP
          </span>
        </div>
      </div>

      {effects && (
        <div className="tooltip-effects">
          {effects.StatBonuses && Object.entries(effects.StatBonuses).some(([, v]) => v > 0) && (
            <div className="tooltip-effects-section">
              <div className="tooltip-section-title">Stat Bonuses:</div>
              {Object.entries(effects.StatBonuses).map(([stat, value]) =>
                value > 0 && (
                  <div key={stat} className="tooltip-effect-item">
                    <span>{humanizeEffectKey(stat)}: +{value}</span>
                  </div>
                )
              )}
            </div>
          )}

          {effects.CombatBonuses && Object.entries(effects.CombatBonuses).some(([, v]) => v > 0) && (
            <div className="tooltip-effects-section">
              <div className="tooltip-section-title">Combat Bonuses:</div>
              {Object.entries(effects.CombatBonuses).map(([bonus, value]) =>
                value > 0 && (
                  <div key={bonus} className="tooltip-effect-item">
                    <span>{humanizeEffectKey(bonus)}: {formatBonusValue(bonus, value)}</span>
                  </div>
                )
              )}
            </div>
          )}

          {effects.PassiveAbilities && effects.PassiveAbilities.length > 0 && (
            <div className="tooltip-effects-section">
              <div className="tooltip-section-title">Passive Ability:</div>
              {effects.PassiveAbilities.map(ability => {
                const abilityDef = findPassiveDefinition(passiveDefinitions, ability);
                return (
                  <div key={ability} className="tooltip-passive-ability">
                    <span className="tooltip-passive-name">{abilityDef?.name ?? ability}</span>
                    {abilityDef && <span className="tooltip-passive-desc">{abilityDef.description}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {skill.cannotLearnReason && <div className="tooltip-warning">{skill.cannotLearnReason}</div>}
      {skill.isLearned && <div className="tooltip-status learned">✓ Learned</div>}
      {skill.canLearn && !skill.isLearned && <div className="tooltip-status can-learn">Click to learn</div>}

      {passiveAbility && <div className="tooltip-passive-desc">{passiveAbility.description}</div>}
    </div>
  );
};

export default SkillTooltip;
